import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { register, createCheckoutSession, api } from '@/api';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useNavigate, Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, Loader2, Rocket, Gift, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

export default function RegisterCompany(){
  const { t } = useLanguage();
  const [companyName,setCompanyName]=useState('');
  const [street,setStreet]=useState('');
  const [number,setNumber]=useState('');
  const [cap,setCap]=useState('');
  const [city,setCity]=useState('');
  const [vatOrUid,setVat]=useState('');
  const [profession,setProfession]=useState('');
  const [email,setEmail]=useState('');
  const [phone,setPhone]=useState('');
  const [password,setPassword]=useState('');
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTrialPopup, setShowTrialPopup] = useState(true);
  const nav=useNavigate();
  
  useEffect(() => {
    api.get('/categories').then(res => {
      const sorted = (res.data || []).sort((a, b) => {
        if (a.name === 'Altro') return 1;
        if (b.name === 'Altro') return -1;
        return a.name.localeCompare(b.name, 'it');
      });
      setCategories(sorted);
    }).catch(console.error);
  }, []);

  const onSubmit=async(e)=>{
    e.preventDefault();
    setIsLoading(true);
    try{
      const registerResponse = await register({
        role:'company', companyName, vatOrUid, email, password, phone, profession, street, number, cap, city
      });
      const token = registerResponse.data?.access_token;
      if (token) sessionStorage.setItem('jc_token', token);
      toast.info(t('registerCompany.redirectToPayment') || 'Reindirizzamento al pagamento...');
      const { data } = await createCheckoutSession('subscription', { reason: 'new_registration', company_name: companyName });
      if(data?.url){ window.location.href = data.url; }
      else { toast.error(t('registerCompany.paymentError') || 'Errore nel reindirizzamento al pagamento'); nav('/ditta/payment-callback?canceled=true'); }
    }catch(err){ toast.error(t('registerCompany.errorMessage')); console.error(err); }
    finally { setIsLoading(false); }
  }

  return(
    <div className="min-h-screen bg-slate-50">
      <Toaster />

      <Dialog open={showTrialPopup} onOpenChange={setShowTrialPopup}>
        <DialogContent className="max-w-md mx-auto bg-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-center text-xl flex items-center justify-center gap-2">
              <Rocket className="w-6 h-6 text-indigo-600" />
              ServiceGo è partita da poco
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center space-y-4">
            <p className="text-slate-600">Nella fase iniziale potresti ricevere meno richieste rispetto al normale.</p>
            <p className="text-slate-600">Per questo vogliamo darti il tempo di testare la piattaforma senza rischi.</p>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
              <div className="flex items-center justify-center gap-2 text-indigo-700 font-bold text-lg">
                <Gift className="w-5 h-5" />3 mesi gratuiti per la tua ditta<Gift className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-500 mt-2">Inserisci i dati della carta per attivare il trial. Il primo addebito avverrà tra 90 giorni.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTrialPopup(false)} className="w-full bg-[#5848FF] hover:bg-[#4638EF] text-white">Ho capito, procedi!</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <button onClick={() => nav(-1)} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-12 sm:h-16 w-auto" /></Link>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Button variant="ghost" onClick={() => nav('/login')} className="text-slate-700">Login</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('registerCompany.title')}</h1>
          <p className="text-slate-500 mt-1">{t('registerCompany.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
              <form onSubmit={onSubmit} className="space-y-6" data-testid="company-register-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.companyName')}</label>
                    <Input data-testid="company-name" placeholder={t('registerCompany.companyNamePlaceholder')} value={companyName} onChange={e=>setCompanyName(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.professionalType')}</label>
                    <Select value={profession} onValueChange={setProfession}>
                      <SelectTrigger data-testid="company-profession" className="h-11 rounded-xl">
                        <SelectValue placeholder={t('registerCompany.professionalTypePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-xl max-h-60">
                        {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.street')}</label>
                    <Input data-testid="company-street" placeholder={t('registerCompany.streetPlaceholder')} value={street} onChange={e=>setStreet(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.number')}</label>
                    <Input data-testid="company-number" placeholder={t('registerCompany.numberPlaceholder')} value={number} onChange={e=>setNumber(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.postalCode')}</label>
                    <Input data-testid="company-cap" placeholder={t('registerCompany.postalCodePlaceholder')} value={cap} onChange={e=>setCap(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.city')}</label>
                    <Input data-testid="company-city" placeholder={t('registerCompany.cityPlaceholder')} value={city} onChange={e=>setCity(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.commerceNumber')}</label>
                  <Input data-testid="company-vat" placeholder={t('registerCompany.commerceNumberPlaceholder')} value={vatOrUid} onChange={e=>setVat(e.target.value)} className="h-11 rounded-xl" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.businessEmail')}</label>
                    <Input data-testid="company-email" autoComplete="email" placeholder={t('registerCompany.businessEmailPlaceholder')} type="email" value={email} onChange={e=>setEmail(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{t('registerCompany.phoneNumber')}</label>
                    <Input data-testid="company-phone" placeholder={t('registerCompany.phoneNumberPlaceholder')} value={phone} onChange={e=>setPhone(e.target.value)} className="h-11 rounded-xl" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Password</label>
                  <Input data-testid="company-password" autoComplete="new-password" placeholder={t('registerCompany.password')} type="password" value={password} onChange={e=>setPassword(e.target.value)} className="h-11 rounded-xl" />
                </div>

                <Button data-testid="company-register-submit" type="submit" className="w-full h-12 bg-[#5848FF] hover:bg-[#4638EF] text-white text-base font-semibold rounded-xl shadow-lg shadow-indigo-200" disabled={isLoading}>
                  {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.loading')}</> : t('registerCompany.subscribeButton')}
                </Button>
                
                <p className="text-xs text-slate-500 text-center">
                  Registrandoti, accetti i nostri{' '}
                  <a href="/termini" className="text-indigo-600 hover:underline">Termini e Condizioni</a>
                  {' '}e la{' '}
                  <a href="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</a>
                </p>
              </form>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white mb-6" data-testid="plan-card">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-emerald-100 font-medium">{t('ui.monthlyPlan')}</span>
                  <span className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-semibold">{t('ui.threeMonthsFree')}</span>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-extrabold">49</span>
                  <span className="text-lg">CHF/mese</span>
                </div>
                <p className="text-emerald-100 text-sm mb-6">{t('ui.threeMonthsFreeThen')}</p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-200 flex-shrink-0"/><span className="text-sm">{t('registerCompany.planFeature1')}</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-200 flex-shrink-0"/><span className="text-sm">{t('registerCompany.planFeature2')}</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-200 flex-shrink-0"/><span className="text-sm">{t('registerCompany.planFeature3')}</span></li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-200 flex-shrink-0"/><span className="text-sm">{t('registerCompany.planFeature4')}</span></li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-900 mb-3">{t('ui.alreadyHaveAccount')}</h3>
                <Button variant="outline" className="w-full rounded-xl" onClick={() => nav('/login')}>{t('ui.login')}</Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
